import { useRef, useState } from "react";
import {
    Search,
    Mail,
    Phone,
    MessageCircle,
    LifeBuoy,
    Ticket,
    Paperclip,
    X,
    FileText,
    Image as ImageIcon,
    Loader2,
} from "lucide-react";
import FaqAccordion from "./FaqAccordion.jsx";
import ContactCard from "./ContactCard";
import TicketsDashboard from "./TicketsDashboard";
import faqData from "./faqData";
import { createSupportTicket } from "../service/Vendorservisefile/suportservises.jsx";

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_MB = 10;

const CATEGORY_OPTIONS = [
    { value: "MACHINE_ISSUE", label: "Machine Issue" },
    { value: "SOFTWARE", label: "Software" },
    { value: "BILLING", label: "Billing" },
    { value: "BOOKING", label: "Booking" },
    { value: "ACCESS", label: "Access" },
    { value: "OTHER", label: "Other" },
];

const PRIORITY_OPTIONS = [
    { value: "LOW", label: "Low" },
    { value: "MEDIUM", label: "Medium" },
    { value: "HIGH", label: "High" },
];

function formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentChip({ attachment, onRemove }) {
    return (
        <div className="group relative flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 pr-2 pl-2 py-1.5">
            {attachment.previewUrl ? (
                <img
                    src={attachment.previewUrl}
                    alt={attachment.name}
                    className="h-8 w-8 shrink-0 rounded-md object-cover"
                />
            ) : (
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-gray-200 text-gray-500">
                    <FileText className="h-4 w-4" />
                </span>
            )}
            <div className="min-w-0 pr-4">
                <p className="max-w-[140px] truncate text-xs font-medium text-gray-700">{attachment.name}</p>
                <p className="text-[11px] text-gray-400">{formatFileSize(attachment.size)}</p>
            </div>
            <button
                type="button"
                onClick={() => onRemove(attachment.id)}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-gray-400 text-white opacity-0 transition group-hover:opacity-100 hover:bg-red-500"
                aria-label={`Remove ${attachment.name}`}
            >
                <X className="h-3 w-3" />
            </button>
        </div>
    );
}

export default function Help() {
    const [activeTab, setActiveTab] = useState("help"); // "help" | "tickets"

    const [query, setQuery] = useState("");
    const [ticketSubject, setTicketSubject] = useState("");
    const [ticketMessage, setTicketMessage] = useState("");
    const [ticketCategory, setTicketCategory] = useState("OTHER");
    const [ticketPriority, setTicketPriority] = useState("LOW");
    const [attachments, setAttachments] = useState([]);
    const [attachmentError, setAttachmentError] = useState("");
    const [tickets, setTickets] = useState([]);

    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");

    const fileInputRef = useRef(null);

    const filteredFaqs = faqData.filter(
        (f) =>
            f.question.toLowerCase().includes(query.toLowerCase()) ||
            f.answer.toLowerCase().includes(query.toLowerCase())
    );

    const handleAttachClick = () => {
        fileInputRef.current?.click();
    };

    const handleFilesSelected = (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ""; // allow re-selecting the same file later

        if (files.length === 0) return;

        setAttachmentError("");

        const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
        const validFiles = files.filter((f) => f.size <= MAX_FILE_SIZE_MB * 1024 * 1024);

        setAttachments((prev) => {
            const room = MAX_ATTACHMENTS - prev.length;
            const toAdd = validFiles.slice(0, Math.max(room, 0));

            if (validFiles.length > toAdd.length || oversized.length > 0) {
                setAttachmentError(
                    oversized.length > 0
                        ? `Some files exceed ${MAX_FILE_SIZE_MB}MB and were skipped.`
                        : `You can attach up to ${MAX_ATTACHMENTS} files.`
                );
            }

            const mapped = toAdd.map((file) => ({
                id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
                name: file.name,
                size: file.size,
                type: file.type,
                previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
            }));

            return [...prev, ...mapped];
        });
    };

    const removeAttachment = (id) => {
        setAttachments((prev) => {
            const target = prev.find((a) => a.id === id);
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((a) => a.id !== id);
        });
    };

    const handleSubmitTicket = async (e) => {
        e.preventDefault();
        if (!ticketSubject.trim() || !ticketMessage.trim() || submitting) return;

        setSubmitting(true);
        setSubmitError("");

        try {
            const created = await createSupportTicket({
                subject: ticketSubject.trim(),
                message: ticketMessage.trim(),
                category: ticketCategory,
                priority: ticketPriority,
            });

            // Attachments aren't part of the API payload (JSON body, no file upload
            // support in this endpoint yet) — kept client-side so they still show
            // up in the local tickets view.
            const newTicket = {
                id: created?.id ?? Date.now(),
                subject: created?.subject ?? ticketSubject.trim(),
                message: created?.message ?? ticketMessage.trim(),
                category: created?.category ?? ticketCategory,
                priority: created?.priority ?? ticketPriority,
                status: created?.status ?? "OPEN",
                createdAt: created?.createdAt ?? new Date().toISOString(),
                attachments,
            };

            setTickets((prev) => [newTicket, ...prev]);

            setTicketSubject("");
            setTicketMessage("");
            setTicketCategory("OTHER");
            setTicketPriority("LOW");
            setAttachments([]);
            setAttachmentError("");
            setActiveTab("tickets");
        } catch (err) {
            setSubmitError(err.message || "Something went wrong while raising the ticket. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="h-screen overflow-y-auto">
            <div className="p-4 sm:p-6">
                {/* Tabs — sit above everything else on the page */}
                <div className=" flex justify-center">
                    <div className="mb-6 w-full flex justify-start">
                        <div className="inline-flex items-center rounded-xl border border-gray-200 bg-white p-1 shadow-sm">
                            <button
                                type="button"
                                onClick={() => setActiveTab("help")}
                                className={`group flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-medium transition-all duration-200 ${activeTab === "help"
                                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-indigo-600"
                                    }`}
                            >
                                <LifeBuoy
                                    className={`h-4 w-4 ${activeTab === "help"
                                        ? "text-white"
                                        : "text-gray-500 group-hover:text-indigo-600"
                                        }`}
                                />
                                <span>Help & FAQ</span>
                            </button>

                            <button
                                type="button"
                                onClick={() => setActiveTab("tickets")}
                                className={`group ml-1 flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-medium transition-all duration-200 ${activeTab === "tickets"
                                    ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-indigo-600"
                                    }`}
                            >
                                <Ticket
                                    className={`h-4 w-4 ${activeTab === "tickets"
                                        ? "text-white"
                                        : "text-gray-500 group-hover:text-indigo-600"
                                        }`}
                                />
                                <span>My Tickets</span>

                                {tickets.length > 0 && (
                                    <span
                                        className={`ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${activeTab === "tickets"
                                            ? "bg-white text-indigo-600"
                                            : "bg-indigo-100 text-indigo-700"
                                            }`}
                                    >
                                        {tickets.length}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {activeTab === "help" ? (
                    <>
                        {/* Header */}
                        <div className="bg-gradient-to-br from-[#1e1b4b] via-[#312e81] to-[#4c1d95] text-white rounded-2xl px-6 py-10 sm:px-8 mb-6">
                            <span className="inline-flex items-center gap-2 text-xs font-semibold bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-4">
                                <LifeBuoy className="w-3.5 h-3.5" /> Support Centre
                            </span>
                            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2">
                                How can we help?
                            </h1>
                            <p className="text-indigo-100 mb-6 max-w-xl">
                                Search our FAQs or reach out directly — our team typically responds
                                within 24 hours.
                            </p>

                            <div className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 max-w-xl">
                                <Search className="w-4 h-4 text-gray-400 shrink-0" />
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search for answers..."
                                    className="w-full text-sm text-gray-900 focus:outline-none"
                                />
                            </div>
                        </div>

                        {/* Contact options */}
                        <div className="grid sm:grid-cols-3 gap-4 mb-8">
                            <ContactCard
                                icon={Mail}
                                iconBg="from-indigo-500 to-purple-600"
                                title="Email us"
                                detail="support@apic.ap.gov.in"
                            />
                            <ContactCard
                                icon={Phone}
                                iconBg="from-sky-400 to-blue-500"
                                title="Call us"
                                detail="+91 891 123 4567"
                            />
                            <ContactCard
                                icon={MessageCircle}
                                iconBg="from-fuchsia-500 to-pink-600"
                                title="Live chat"
                                detail="Mon-Sat, 9am-6pm IST"
                            />
                        </div>

                        <div className="grid lg:grid-cols-3 gap-6">
                            {/* FAQs */}
                            <div className="lg:col-span-2">
                                <h2 className="font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
                                {filteredFaqs.length > 0 ? (
                                    <FaqAccordion faqs={filteredFaqs} />
                                ) : (
                                    <div className="h-32 flex items-center justify-center text-gray-400 border border-dashed border-gray-200 rounded-2xl">
                                        No results for "{query}"
                                    </div>
                                )}
                            </div>

                            {/* Raise a ticket */}
                            <div className="bg-white border border-gray-100 rounded-2xl p-6 h-fit">
                                <h2 className="font-bold text-gray-900 mb-1">Raise a ticket</h2>
                                <p className="text-sm text-gray-500 mb-5">
                                    Can't find what you're looking for? Send us a message.
                                </p>

                                <form className="space-y-4" onSubmit={handleSubmitTicket}>
                                    <label className="block">
                                        <span className="block text-sm font-medium text-gray-700 mb-1.5">Subject</span>
                                        <input
                                            value={ticketSubject}
                                            onChange={(e) => setTicketSubject(e.target.value)}
                                            placeholder="e.g. Delay in EV Battery Enclosure v2"
                                            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        />
                                    </label>

                                    <div className="grid grid-cols-2 gap-3">
                                        <label className="block">
                                            <span className="block text-sm font-medium text-gray-700 mb-1.5">Category</span>
                                            <select
                                                value={ticketCategory}
                                                onChange={(e) => setTicketCategory(e.target.value)}
                                                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            >
                                                {CATEGORY_OPTIONS.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>

                                        <label className="block">
                                            <span className="block text-sm font-medium text-gray-700 mb-1.5">Priority</span>
                                            <select
                                                value={ticketPriority}
                                                onChange={(e) => setTicketPriority(e.target.value)}
                                                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                            >
                                                {PRIORITY_OPTIONS.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                        {opt.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>

                                    <label className="block">
                                        <span className="block text-sm font-medium text-gray-700 mb-1.5">Message</span>
                                        <textarea
                                            value={ticketMessage}
                                            onChange={(e) => setTicketMessage(e.target.value)}
                                            placeholder="Describe your issue..."
                                            rows={4}
                                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                        />
                                    </label>

                                    {/* Attachments */}
                                    <div>
                                        <span className="block text-sm font-medium text-gray-700 mb-1.5">
                                            Attachments <span className="text-gray-400 font-normal">(optional)</span>
                                        </span>

                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            multiple
                                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                            onChange={handleFilesSelected}
                                            className="hidden"
                                        />

                                        <button
                                            type="button"
                                            onClick={handleAttachClick}
                                            disabled={attachments.length >= MAX_ATTACHMENTS}
                                            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-500 transition hover:border-indigo-400 hover:bg-indigo-50 hover:text-indigo-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-gray-300 disabled:hover:bg-gray-50 disabled:hover:text-gray-500"
                                        >
                                            <Paperclip className="h-4 w-4" />
                                            Attach images or files
                                        </button>

                                        <p className="mt-1.5 text-xs text-gray-400">
                                            Up to {MAX_ATTACHMENTS} files, {MAX_FILE_SIZE_MB}MB each — images, PDFs, or docs.
                                        </p>

                                        {attachmentError && (
                                            <p className="mt-1.5 text-xs text-red-500">{attachmentError}</p>
                                        )}

                                        {attachments.length > 0 && (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {attachments.map((a) => (
                                                    <AttachmentChip key={a.id} attachment={a} onRemove={removeAttachment} />
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    {submitError && (
                                        <p className="text-xs font-medium text-red-500">{submitError}</p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="flex w-full items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-blue-500 text-white font-semibold text-sm py-3 rounded-xl hover:opacity-90 transition disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                Submitting...
                                            </>
                                        ) : (
                                            "Submit Ticket"
                                        )}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </>
                ) : (
                    <TicketsDashboard tickets={tickets} />
                )}
            </div>
        </div>
    );
}